/**
 * Proportional Progress Bar Component
 * 
 * A reusable component that displays a horizontal progress bar with multiple segments,
 * where each segment's width is proportional to its value relative to the total sum.
 * Each segment shows its value below and displays a tooltip with description on hover.
 */

import React from 'react';
import {
  Box,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';

export interface ProgressSegment {
  value: number;
  description: string;
  color?: string; // Optional custom color, will use theme colors if not provided
}

interface ProportionalProgressBarProps {
  segments: ProgressSegment[];
  height?: number;
  gap?: number; // Gap between segments in pixels
  showValues?: boolean;
  valueColor?: string;
  containerWidth?: string | number;
  className?: string;
}

// Default green color palette for segments (used when no color is provided)
const DEFAULT_COLORS = [
  '#4ade80', // light green
  '#22c55e', // green
  '#16a34a', // emerald
  '#15803d', // dark green
  '#166534', // darker green
  '#14532d', // darkest green
];

const MIN_LABEL_WIDTH_PX = 20; // Minimal width to reduce overlap without large shifts

export const ProportionalProgressBar: React.FC<ProportionalProgressBarProps> = ({
  segments,
  height = 8,
  gap = 6, // Slightly larger default gap between segments
  showValues = true,
  valueColor,
  containerWidth = '100%',
  className,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const hoverColor = theme.palette.primary.dark || '#1d4ed8';
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  // Use all segments (including zeros) - always show every section
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  if (segments.length === 0) {
    return (
      <Box
        className={className}
        sx={{
          width: containerWidth,
          height: showValues ? height + 24 : height,
        }}
      >
        <Box
          sx={{
            width: '100%',
            height,
            backgroundColor: theme.palette.grey[200],
            borderRadius: height / 2,
          }}
        />
      </Box>
    );
  }

  // Add 5% of total to each value for bar proportions to prevent tiny segments from colliding
  const MIN_PORTION_FLOOR = 0.05;
  const adjustedTotal =
    total +
    segments.length * MIN_PORTION_FLOOR * (total || 1);
  const getAdjustedValue = (value: number) =>
    value + MIN_PORTION_FLOOR * (total || 1);

  // When total is 0, give equal width to each segment
  const segmentData = segments.map((segment, index) => {
    const adjustedValue =
      adjustedTotal > 0 ? getAdjustedValue(segment.value) : 1;
    const portion = adjustedTotal > 0 ? adjustedValue / adjustedTotal : 1 / segments.length;
    const percentage = portion * 100;
    const color = segment.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];

    return {
      ...segment,
      percentage,
      color,
      startPosition:
        segments
          .slice(0, index)
          .reduce(
            (sum, s) =>
              sum +
              (adjustedTotal > 0
                ? getAdjustedValue(s.value) / adjustedTotal
                : 1 / segments.length),
            0
          ) * 100,
    };
  });

  return (
    <Box
      className={className}
      sx={{
        width: containerWidth,
        height: showValues ? height + 32 : height, // Increased space for values
        display: 'flex',
        flexDirection: 'column',
        gap: 2, // Increased gap between progress bar and values
      }}
    >
      {/* Progress Bar */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height,
          backgroundColor: theme.palette.grey[200],
          borderRadius: height / 2,
          overflow: 'hidden',
        }}
      >
        {segmentData.map((segment, index) => (
          <Tooltip
            key={index}
            title={segment.description}
            placement="top"
            arrow
            enterDelay={200}
            leaveDelay={0}
          >
            <Box
              sx={{
                position: 'absolute',
                left: `${segment.startPosition}%`,
                width: `${segment.percentage}%`,
                height: '100%',
                  backgroundColor: hoveredIndex === index ? hoverColor : segment.color,
                borderRadius: height / 2,
                cursor: 'pointer',
                transition: 'background-color 0.2s ease, opacity 0.2s ease',
                '&:hover': {
                    backgroundColor: hoverColor,
                },
                // Add gap between segments (except for the last one)
                ...(index < segmentData.length - 1 && {
                  marginRight: gap,
                  width: `calc(${segment.percentage}% - ${gap}px)`,
                }),
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(prev => (prev === index ? null : prev))}
            />
          </Tooltip>
        ))}
      </Box>

      {/* Values Display */}
      {showValues && (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 32, // Increased height for better spacing
          }}
        >
          {segmentData.map((segment, index) => (
            <Tooltip
              key={index}
              title={segment.description}
              placement="bottom"
              arrow
              enterDelay={200}
              leaveDelay={0}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: `${segment.startPosition}%`,
                  minWidth: MIN_LABEL_WIDTH_PX,
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  cursor: 'pointer',
                  height: '100%',
                  '&:hover .progress-label-text': {
                    color: hoverColor,
                  },
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(prev => (prev === index ? null : prev))}
              >
                <Typography
                  variant={isMobile ? 'caption' : 'body2'}
                  sx={{
                    fontWeight: 500,
                    color: valueColor || (hoveredIndex === index ? hoverColor : segment.color),
                    textAlign: 'center',
                    lineHeight: 1.2,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    whiteSpace: 'nowrap',
                  }}
                  className="progress-label-text"
                >
                  {segment.value.toLocaleString()}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ProportionalProgressBar;
